import React, { useState, useEffect, useMemo, useContext, useRef } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Alert,
  Button,
  FormGroup,
  Label,
  Input,
  InputGroup,
  InputGroupAddon,
  Row,
  Col,
  FormText,
  Badge,
  Progress,
} from "reactstrap";
import { Link } from "react-router-dom";
import Select from "react-select";
import LoaderInner from "../../components/LoaderInner";
import TableHelper from "../../components/tableHelper";
import StripeSubscriptionConfigEventsTable from "../../components/stripeSubscriptionConfigEventsTable";
import SubscriptionsFeatureLockAlerts from "../../components/subscriptionsFeatureLockAlerts";
import { stripeSubscriptionService } from "../../jwt/_services/stripeSubscription.service";
import TenantContext from "../../utils/tenantContext";
import { StateContext } from "../../utils/context";
import { isSubscriptionFeatureLocked } from "../../utils/subscriptionUtils";
import { STRIPE_CURRENCY_OPTIONS, TAX_CALCULATION_OPTIONS } from "./stripeConstants";
import "./stripeSubscriptionConfig.css";

const MASKED_SECRET = "sk_••••••••••••••••••••••••••••";
const MASKED_WHSEC = "whsec_••••••••••••••••••••";

const buildReadinessChecks = (config, products) => {
  const webhookReady = config?.webhook_endpoint_id && !config?.webhook_manual_required;
  const hasProducts = (products || []).length > 0;

  return [
    {
      id: "secret_key",
      label: "Secret API key configured",
      status: config?.has_secret_key ? "pass" : "fail",
      detail: config?.has_secret_key ? config.stripe_account_name : "Connect your Stripe secret key",
    },
    {
      id: "webhook_endpoint",
      label: "Webhook endpoint active",
      status: webhookReady ? "pass" : config?.has_secret_key ? "warn" : "fail",
      detail: webhookReady ? "Endpoint registered in Stripe" : config?.webhook_manual_required ? "Configure webhook manually in Stripe" : "Registered automatically after connecting Stripe",
    },
    {
      id: "signing_secret",
      label: "Signing secret saved",
      status: webhookReady || config?.has_webhook_signing_secret ? "pass" : config?.webhook_manual_required ? "warn" : "fail",
      detail: config?.webhook_manual_required ? "Paste signing secret from Stripe" : "Required for webhook verification",
    },
    {
      id: "events",
      label: `Core events subscribed (${(config?.required_webhook_events || []).length})`,
      status: webhookReady && (config?.required_webhook_events || []).length > 0 ? "pass" : config?.has_secret_key ? "warn" : "fail",
      detail: "Subscription lifecycle and invoice events",
    },
    {
      id: "live_mode",
      label: config?.has_secret_key
        ? (config.stripe_livemode ? "Live mode keys" : "Test mode keys")
        : "Live mode keys",
      status: config?.stripe_livemode ? "pass" : config?.has_secret_key ? "warn" : "fail",
      detail: config?.has_secret_key
        ? (config.stripe_livemode ? "Production-ready keys" : "Switch to live keys before launch")
        : "Connect Stripe to detect key mode",
    },
    {
      id: "products",
      label: hasProducts ? `${products.length} product${products.length !== 1 ? "s" : ""} configured` : "Products created",
      status: hasProducts ? "pass" : "fail",
      detail: hasProducts ? "Plans available for subscription" : "Add plans in Products",
    },
  ];
};

const getProgressFromChecks = (checks) => {
  if (!checks.length) return 0;
  const completed = checks.filter((c) => c.status === "pass").length;
  return Math.round((completed / checks.length) * 100);
};

const StripeSubscriptionConfig = () => {
  const { tenant } = useContext(TenantContext);
  const { currentUser } = useContext(StateContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [copiedField, setCopiedField] = useState("");

  const [config, setConfig] = useState(null);
  const [products, setProducts] = useState([]);
  const [secretKey, setSecretKey] = useState("");
  const [webhookSigningSecret, setWebhookSigningSecret] = useState("");
  const [currency, setCurrency] = useState("eur");
  const [taxCalculationMethod, setTaxCalculationMethod] = useState("inclusive");
  const [savedCurrency, setSavedCurrency] = useState("eur");
  const [savedTaxMethod, setSavedTaxMethod] = useState("inclusive");
  const [savingBilling, setSavingBilling] = useState(false);
  const webhookSyncAttempted = useRef(false);

  const featureLocked = isSubscriptionFeatureLocked(tenant, currentUser?.sla_level);
  const stripeConnected = !!config?.has_secret_key;

  const loadData = () => {
    setLoading(true);
    setError("");
    Promise.all([
      stripeSubscriptionService.getConfig(),
      stripeSubscriptionService.listProducts(),
    ])
      .then(([configData, productsData]) => {
        setConfig(configData);
        const configCurrency = configData.currency || "eur";
        const configTax = configData.tax_calculation_method || "inclusive";
        setCurrency(configCurrency);
        setTaxCalculationMethod(configTax);
        setSavedCurrency(configCurrency);
        setSavedTaxMethod(configTax);
        setProducts(productsData.results || productsData || []);
      })
      .catch(() => setError("Failed to load subscription configuration."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
  }, []);

  const registerWebhook = (successMessage = "Webhook endpoint registered in Stripe.") => {
    setSyncing(true);
    setError("");
    return stripeSubscriptionService
      .syncWebhook()
      .then((data) => {
        setConfig(data);
        setSuccess(successMessage);
        return loadData();
      })
      .catch((err) => {
        const data = err?.response?.data;
        if (data) setConfig(data);
        setError(data?.last_webhook_sync_error || "Webhook registration failed. Use manual setup below.");
      })
      .finally(() => setSyncing(false));
  };

  useEffect(() => {
    if (featureLocked || loading || syncing || !config?.has_secret_key || webhookSyncAttempted.current) return;
    if (config.webhook_endpoint_id && !config.webhook_manual_required) return;
    if (config.webhook_manual_required) return;

    webhookSyncAttempted.current = true;
    registerWebhook();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, loading, syncing]);

  const webhookReady = config?.webhook_endpoint_id && !config?.webhook_manual_required;
  const tenantId = tenant?.id ?? config?.tenant_id;

  const webhookUrl = useMemo(
    () => stripeSubscriptionService.buildWebhookUrl(tenantId),
    [tenantId]
  );

  const readinessChecks = useMemo(
    () => buildReadinessChecks(config, products),
    [config, products]
  );

  const progressScore = useMemo(() => getProgressFromChecks(readinessChecks), [readinessChecks]);
  const attentionCount = readinessChecks.filter((c) => c.status !== "pass").length;

  const selectedCurrency = STRIPE_CURRENCY_OPTIONS.find((o) => o.value === currency)
    || STRIPE_CURRENCY_OPTIONS.find((o) => o.value === "eur");
  const selectedTaxMethod = TAX_CALCULATION_OPTIONS.find((o) => o.value === taxCalculationMethod)
    || TAX_CALCULATION_OPTIONS[0];
  const billingSettingsChanged = currency !== savedCurrency || taxCalculationMethod !== savedTaxMethod;

  const handleCopy = (value, field) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    setCopiedField(field);
    setTimeout(() => setCopiedField(""), 2000);
  };

  const handleConnectStripe = () => {
    if (featureLocked) {
      return;
    }
    if (!secretKey.trim()) {
      setError("Please enter your Stripe secret API key.");
      return;
    }
    setSaving(true);
    setError("");
    setSuccess("");
    stripeSubscriptionService
      .saveConfig({
        secret_key: secretKey.trim(),
      })
      .then((data) => {
        setConfig(data);
        setSecretKey("");
        setSuccess("Stripe account connected successfully.");
        const webhookActive = data.webhook_endpoint_id && !data.webhook_manual_required;
        if (webhookActive) {
          loadData();
          return;
        }
        if (!data.webhook_manual_required) {
          webhookSyncAttempted.current = true;
          return registerWebhook();
        }
        loadData();
      })
      .catch((err) => {
        setError(err?.response?.data?.error || "Failed to connect Stripe account.");
      })
      .finally(() => setSaving(false));
  };

  const handleSaveBillingSettings = () => {
    setSavingBilling(true);
    setError("");
    setSuccess("");
    stripeSubscriptionService
      .saveConfig({
        currency,
        tax_calculation_method: taxCalculationMethod,
      })
      .then((data) => {
        setConfig(data);
        const configCurrency = data.currency || currency;
        const configTax = data.tax_calculation_method || taxCalculationMethod;
        setCurrency(configCurrency);
        setTaxCalculationMethod(configTax);
        setSavedCurrency(configCurrency);
        setSavedTaxMethod(configTax);
        setSuccess("Billing settings saved.");
        loadData();
      })
      .catch((err) => {
        setError(err?.response?.data?.error || "Failed to save billing settings.");
      })
      .finally(() => setSavingBilling(false));
  };

  const handleSaveWebhookSecret = () => {
    if (!webhookSigningSecret.trim()) {
      setError("Please enter the webhook signing secret.");
      return;
    }
    setSaving(true);
    setError("");
    setSuccess("");
    stripeSubscriptionService
      .saveConfig({ webhook_signing_secret: webhookSigningSecret.trim() })
      .then((data) => {
        setConfig(data);
        setWebhookSigningSecret("");
        setSuccess("Webhook signing secret saved.");
        loadData();
      })
      .catch(() => setError("Failed to save webhook signing secret."))
      .finally(() => setSaving(false));
  };

  const checklistIcon = (status) => {
    if (status === "pass") return <i className="mdi mdi-check-circle text-success checklist-icon" />;
    if (status === "warn") return <i className="mdi mdi-alert-circle text-warning checklist-icon" />;
    return <i className="mdi mdi-close-circle text-danger checklist-icon" />;
  };

  return (
    <div className="stripe-subscription-config stripe-subscription-page">
      <LoaderInner show={loading || saving || syncing || savingBilling} />

      <Row className="mb-4 align-items-start">
        <Col>
          <h4 className="mb-1 config-page-title">Subscription Configuration</h4>
          <p className="text-muted mb-0 stripe-page-subtitle">
            Manage configuration for Stripe credentials, webhook and subscription products.
          </p>
        </Col>
      </Row>

      {error && <Alert color="danger">{error}</Alert>}
      {success && <Alert color="success">{success}</Alert>}

      {featureLocked && (
        <SubscriptionsFeatureLockAlerts tenant={tenant} slaLevel={currentUser?.sla_level} />
      )}

      <Row>
        <Col lg={8}>
          <Card className="mb-4 stripe-section-card">
            <CardHeader className="stripe-section-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">API Credentials</h5>
              {config?.has_secret_key && (
                <Badge
                  color={config.stripe_livemode ? "success" : "secondary"}
                  className="stripe-corner-mode-badge"
                >
                  {config.stripe_livemode ? "Live mode" : "Test mode"}
                </Badge>
              )}
            </CardHeader>
            <CardBody>
              <FormGroup>
                <Label>Secret API Key</Label>
                {config?.has_secret_key && (
                  <InputGroup className="mb-2">
                    <Input type="password" value={MASKED_SECRET} readOnly />
                    <InputGroupAddon addonType="append">
                      <Button color="secondary" className="stripe-field-addon-btn" disabled title="Stored securely">
                        <i className="fa fa-lock" />
                      </Button>
                    </InputGroupAddon>
                  </InputGroup>
                )}
                <InputGroup>
                  <Input
                    type="password"
                    value={secretKey}
                    onChange={(e) => setSecretKey(e.target.value)}
                    placeholder={config?.has_secret_key ? "Enter new key to replace..." : "sk_test_... or sk_live_..."}
                    disabled={featureLocked || loading || saving}
                  />
                </InputGroup>
                <FormText color="muted">
                  Get your key from{" "}
                  <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer">
                    Stripe Dashboard
                  </a>
                  . Stored securely and not shown again after saving.
                </FormText>
              </FormGroup>

              <div className="d-flex justify-content-end">
                <Button color="success" disabled={featureLocked || loading || saving} onClick={handleConnectStripe}>
                  {config?.has_secret_key ? "Save" : "Connect"}
                </Button>
              </div>
            </CardBody>
          </Card>

          {stripeConnected && (
            <Card className="mb-4 stripe-section-card">
              <CardHeader className="stripe-section-header">
                <h5 className="mb-0">Billing Settings</h5>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Currency</Label>
                      <Select
                        components={{ IndicatorSeparator: () => null }}
                        options={STRIPE_CURRENCY_OPTIONS}
                        value={selectedCurrency}
                        onChange={(option) => setCurrency(option?.value || "eur")}
                        isDisabled={featureLocked || loading || savingBilling}
                      />
                      <FormText color="muted">
                        Applies to all subscription products and Stripe prices.
                      </FormText>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Tax Calculation</Label>
                      <Select
                        components={{ IndicatorSeparator: () => null }}
                        options={TAX_CALCULATION_OPTIONS}
                        value={selectedTaxMethod}
                        onChange={(option) => setTaxCalculationMethod(option?.value || "inclusive")}
                        isDisabled={featureLocked || loading || savingBilling}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end">
                  <Button
                    color="success"
                    disabled={featureLocked || loading || savingBilling || !billingSettingsChanged}
                    onClick={handleSaveBillingSettings}
                  >
                    Save
                  </Button>
                </div>
              </CardBody>
            </Card>
          )}

          {config?.has_secret_key && (
          <Card className="mb-4 stripe-section-card">
            <CardHeader className="stripe-section-header">
              <h5 className="mb-0">Webhook</h5>
            </CardHeader>
            <CardBody>
              {webhookReady ? (
                <Alert color="success" className="py-2 mb-3">Webhook endpoint is active in Stripe.</Alert>
              ) : syncing ? (
                <Alert color="info" className="py-2 mb-3">
                  Registering your webhook endpoint in Stripe...
                </Alert>
              ) : (
                <Alert color="info" className="py-2 mb-3">
                  This endpoint is registered automatically in Stripe when you connect your account.
                </Alert>
              )}

              <FormGroup>
                <Label>Endpoint URL</Label>
                <InputGroup>
                  <Input value={webhookUrl} readOnly />
                  <InputGroupAddon addonType="append">
                    <Button
                      color="secondary"
                      className="stripe-field-addon-btn"
                      onClick={() => handleCopy(webhookUrl, "webhook_url")}
                    >
                      <i className="fa fa-copy" />
                    </Button>
                  </InputGroupAddon>
                </InputGroup>
                {copiedField === "webhook_url" && (
                  <FormText color="success">Copied to clipboard</FormText>
                )}
              </FormGroup>

              {config?.webhook_endpoint_id && (
                <FormGroup>
                  <Label>Stripe Endpoint ID</Label>
                  <Input value={config.webhook_endpoint_id} readOnly />
                </FormGroup>
              )}

              {(webhookReady || config?.has_webhook_signing_secret || config?.webhook_manual_required) && (
                <FormGroup>
                  <Label>Signing Secret</Label>
                  {(webhookReady || config?.has_webhook_signing_secret) && (
                    <InputGroup className="mb-2">
                      <Input type="password" value={MASKED_WHSEC} readOnly />
                      <InputGroupAddon addonType="append">
                        <Button color="secondary" className="stripe-field-addon-btn" disabled title="Secret is stored securely">
                          <i className="fa fa-lock" />
                        </Button>
                      </InputGroupAddon>
                    </InputGroup>
                  )}
                  {config?.webhook_manual_required && (
                    <InputGroup>
                      <Input
                        type="password"
                        value={webhookSigningSecret}
                        onChange={(e) => setWebhookSigningSecret(e.target.value)}
                        placeholder="whsec_..."
                        disabled={featureLocked || loading || saving}
                      />
                      <InputGroupAddon addonType="append">
                        <Button color="primary" disabled={featureLocked || loading || saving} onClick={handleSaveWebhookSecret}>
                          Save
                        </Button>
                      </InputGroupAddon>
                    </InputGroup>
                  )}
                  {config?.webhook_manual_required && (
                    <FormText color="muted">
                      Paste the signing secret from your Stripe webhook endpoint.
                    </FormText>
                  )}
                </FormGroup>
              )}

              {config?.webhook_manual_required && (
                <Alert color="warning" className="mb-3">
                  Automatic webhook creation failed. Add the endpoint manually in Stripe, select required events, then save the signing secret.
                </Alert>
              )}

              {config?.last_webhook_sync_error && (
                <Alert color="danger" className="mb-3">{config.last_webhook_sync_error}</Alert>
              )}

              <hr className="my-4" />

              <h6 className="mb-3">Subscribed Events</h6>
              {loading ? (
                <TableHelper loading />
              ) : (
                <StripeSubscriptionConfigEventsTable
                  events={config?.required_webhook_events}
                  webhookReady={webhookReady}
                />
              )}
            </CardBody>
          </Card>
          )}
        </Col>

        <Col lg={4}>
          {stripeConnected ? (
            <>
              <Card className="mb-4 stripe-section-card">
                <CardHeader className="stripe-section-header">
                  <h5 className="mb-0">Setup Progress</h5>
                </CardHeader>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="health-score">{progressScore}%</span>
                    <Badge color={progressScore >= 80 ? "success" : progressScore >= 50 ? "warning" : "danger"} className="mode-badge">
                      {progressScore >= 80 ? "Complete" : progressScore >= 50 ? "In progress" : "Pending"}
                    </Badge>
                  </div>
                  <Progress value={progressScore} color={progressScore >= 80 ? "success" : progressScore >= 50 ? "warning" : "danger"} />
                  {attentionCount > 0 && (
                    <FormText color="muted" className="mt-2 d-block stripe-small">
                      {attentionCount} check{attentionCount !== 1 ? "s" : ""} still require attention.
                    </FormText>
                  )}
                </CardBody>
              </Card>

              <Card className="mb-4 stripe-section-card">
                <CardHeader className="stripe-section-header">
                  <h5 className="mb-0">Readiness Checklist</h5>
                </CardHeader>
                <CardBody className="pt-2">
                  {readinessChecks.map((check) => (
                    <div key={check.id} className="checklist-item">
                      {checklistIcon(check.status)}
                      <div>
                        <div className="stripe-check-title">{check.label}</div>
                        <div className="text-muted stripe-small">{check.detail}</div>
                      </div>
                    </div>
                  ))}
                </CardBody>
              </Card>

              <Card className="mb-4 stripe-section-card">
                <CardHeader className="stripe-section-header d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Products</h5>
                  {featureLocked ? (
                    <Button color="primary" outline className="btn-sm" disabled>
                      Manage
                    </Button>
                  ) : (
                    <Link
                      to="/products"
                      className="btn btn-primary btn-outline btn-sm"
                    >
                      Manage
                    </Link>
                  )}
                </CardHeader>
                <CardBody className="pt-2 pb-2">
                  {loading ? (
                    <p className="text-muted stripe-small mb-0">Loading plans...</p>
                  ) : products.length === 0 ? (
                    <p className="text-muted stripe-small mb-0">No plans configured yet.</p>
                  ) : (
                    <ul className="stripe-products-list mb-0">
                      {products.map((product) => (
                        <li key={product.id} className="stripe-products-list-item">
                          <div className="stripe-products-list-main">
                            <span className="stripe-products-list-name">{product.name}</span>
                            {product.price_display && (
                              <span className="stripe-products-list-price">{product.price_display}</span>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardBody>
              </Card>
            </>
          ) : (
            <Card className="mb-4 stripe-section-card">
              <CardHeader className="stripe-section-header">
                <h5 className="mb-0">Setup Progress</h5>
              </CardHeader>
              <CardBody>
                <Alert color="info" className="mb-0">
                  Connect your Stripe secret API key to unlock billing settings, setup progress and product management.
                </Alert>
              </CardBody>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default StripeSubscriptionConfig;
